import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Link, Stack, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Button, brandText } from '@repo/ui'
import { useSupportListRoute } from 'hooks/api'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel,
// so going through it would be a cycle. Same reason `SupportTypeBadge` states.
import { AnnouncementKindBadge } from '../AnnouncementKindBadge/AnnouncementKindBadge'
import { useAnnouncementCta } from './useAnnouncementCta'
import { useClampOverflow } from 'hooks'
import { ROUTES } from 'routes'
import { isRichTextBody, sanitizeAnnouncementBody } from 'utils/announcementBody'
import type { IAnnouncement } from 'services/announcementsApi'

/**
 * ONE announcement, rendered identically in ALL THREE places it appears (§36):
 * the once-per-session modal, the `/whats-new` archive feed, and the `/whats-new/:id`
 * detail page.
 *
 * The whole reason this is a component and not three blocks of JSX: those surfaces
 * show the same content and must not drift. The FIRST hand-roll would have been the
 * modal; the second would have been the page — and §36 says the second is a shared
 * component. Concretely, drift here would mean the modal offering a CTA that the page
 * does not, so a user who dismissed the modal loses the only path to the feature. The
 * detail page was the third, and it re-renders NOTHING: it sets a variant.
 *
 * `variant` changes chrome ONLY — never content, never whether the CTA exists:
 *   - `modal`  — inside the dialog, so no card border and no CTA BUTTON (the dialog IS
 *                the frame, and supplies both — see below)
 *   - `page`   — a bordered card in the archive list, body CLAMPED (see below), title
 *                links to the detail page
 *   - `detail` — the full body at `/whats-new/:id`. No border and no title, because the
 *                page's `Header` is the frame and already carries the title; rendering
 *                it again inside the card would print it twice.
 *
 * ## The `modal` variant does not paint the CTA, and that is still the contract
 *
 * Read literally, "never whether the CTA exists" and "`modal` renders no CTA button"
 * look like a contradiction. They are not, and the distinction is worth stating rather
 * than leaving for whoever reads this next to re-derive.
 *
 * The invariant is about the OFFER, not the pixels: every surface that shows an
 * announcement must give the reader the same way into the feature, because dismissal is
 * permanent and a reader who dismissed the modal must not have lost a path the archive
 * never had. A CTA the modal simply dropped would break that. A CTA the modal renders in
 * its FOOTER does not — it is the same button, with the same label, running the same
 * handler (`useAnnouncementCta`, shared by both so the behaviour cannot drift), one
 * container further out.
 *
 * It is the same move this variant already makes for the border: `modal` draws none
 * because the dialog draws it instead. The CTA now works the same way. The footer is
 * where a dialog's actions belong, and it is the only place the CTA can sit and still
 * be the single rightmost primary next to "Got it" and "See all" (§26/§33) rather than
 * a third button stranded mid-body above two more.
 *
 * WHAT WOULD ACTUALLY VIOLATE THE CONTRACT: a surface where `hasCta` is true and no
 * button of any kind is reachable. So if you add a fourth variant, decide explicitly
 * which frame paints the button — and if the frame does not, the card must.
 *
 * ## Why `page` clamps and the other two do not
 *
 * Bodies are free text with NO length limit — the internal editor is a plain textarea,
 * and the seeded deprecation notice is already several times the length of everything
 * around it. The archive renders every announcement the reader has, so one long notice
 * makes the entire feed unscannable and pushes every older item below the fold. The
 * clamp is three lines; the detail page is where the rest lives.
 *
 * The clamp is MEASURED, not assumed. "Read more" renders only when the body actually
 * overflows its three lines — a two-line announcement would otherwise grow a link that
 * leads to a page showing the identical text, which reads as a bug and trains readers
 * to ignore the affordance on the announcements where it matters.
 */
interface Props {
  announcement: IAnnouncement
  variant?: 'modal' | 'page' | 'detail'
  /**
   * Called after the CTA is activated. The modal passes its close handler: taking
   * the action is also dismissing, and leaving the dialog open over the page it just
   * navigated to would cover the thing the user came to look at.
   */
  onActioned?: () => void
  /**
   * Product-tour anchor for THIS card, emitted as `data-tour` on the root (§56).
   *
   * A prop rather than a wrapper element, for the reason the shared `Table` takes the
   * same one: the anchor has to sit on the thing being ringed, and the spotlight is
   * measured from `getBoundingClientRect`, so an extra wrapping `<Box>` would either
   * ring a box that is not the card or need its own sizing to pretend it is.
   *
   * Only the archive's FIRST card passes it — see `WhatsNewPage`. Left undefined the
   * attribute is not emitted at all, which is what the modal and detail variants want:
   * two elements answering the same `data-tour` selector would make which one gets
   * ringed a function of DOM order.
   */
  dataTour?: string
}

/**
 * Lines of body shown in the archive feed before "Read more".
 *
 * Three, not two or five. Two truncates most announcements — the majority run three to
 * four lines at the archive's column width, so a two-line clamp would put a "Read more"
 * on nearly every card and make the shared component's job be hiding content rather
 * than showing it. Five stops clamping the thing the clamp exists for: measured against
 * the seeded deprecation notice, five lines still fills most of a viewport.
 */
const FEED_BODY_LINES = 3

export const AnnouncementCard = ({
  announcement,
  variant = 'page',
  onActioned,
  dataTour,
}: Props) => {
  const theme = useTheme()
  const navigate = useNavigate()
  /*
    The FALLBACK for a "you asked for this" title with no resolvable request behind it
    (VRG-324 retired the board this used to point at). Permission-aware and shared with
    the retired board's redirect, so one rule decides it — see `useSupportListRoute`.
    The primary destination is the request itself; see the links below.
  */
  const requestsRoute = useSupportListRoute()

  // The CTA's label, whether there is one, what activating it does, and the referrer
  // every outbound link here carries — all from one place, because the modal's FOOTER
  // renders the same button and the two must not drift (§36). See `useAnnouncementCta`.
  const { hasCta, ctaLabel, referrer, onCta } = useAnnouncementCta(announcement, onActioned)

  const isDetail = variant === 'detail'
  /**
   * The dialog paints its own CTA in the footer, so the card must not paint a second
   * one in the body. This is the ONLY variant where the frame supplies the button —
   * see the "still the contract" section of the docblock before adding another.
   */
  const isModal = variant === 'modal'
  /** The archive feed — the only variant that clamps and the only one that links out. */
  const isFeed = variant === 'page'

  /**
   * Rich text, or the plain text every body used to be?
   *
   * Decides the RENDERING branch only — both are XSS-safe, so a wrong answer here is
   * cosmetic rather than dangerous (see `isRichTextBody`). Bodies are immutable in
   * practice once published, but this is memoised anyway because the feed re-renders on
   * every hover and the regex would otherwise run per card per frame.
   */
  const isRich = useMemo(() => isRichTextBody(announcement.body), [announcement.body])

  /**
   * DOES the body actually overflow its clamp?
   *
   * The measurement — and the four details that make it correct (callback ref,
   * 1px sub-pixel slack, ResizeObserver re-measure, jsdom guard) — moved to the
   * shared `useClampOverflow` when the product page's clamped description needed
   * the same thing with a different affordance (§36: extract on the second
   * hand-roll). The reasoning now lives in that hook's docblock; this is a
   * strict reduction of what was here, not a behaviour change.
   *
   * Only the feed clamps, so only the feed can overflow.
   */
  const { ref: setBodyRef, overflows } = useClampOverflow(isFeed, announcement.body)

  /**
   * Open the detail page, carrying the referrer (§42).
   *
   * `/whats-new` is an ANCESTOR of `/whats-new/:id`, so `ReferrerBackLink` deliberately
   * suppresses itself here and the BREADCRUMB is the way back — that is the rule for a
   * hierarchical drill-in, and showing both would be two controls with one meaning.
   * The referrer is still worth passing: `Breadcrumbs` swaps the parent crumb's bare
   * route for `backTo` (pathname + search), so returning restores the archive exactly
   * as the reader left it rather than a reset page.
   */
  const openDetail = () => {
    onActioned?.()
    navigate(ROUTES.WHATS_NEW_ANNOUNCEMENT(announcement.id), { state: referrer })
  }

  return (
    <Stack
      spacing={3}
      data-tour={dataTour}
      /*
        In the FEED the whole card is the affordance, exactly as a support request row
        is (`SupportPage.tsx`) — hover moves the CARD's border, not an underline under
        its title. Same pattern, same numbers, and §34's action-row case: clicking
        navigates rather than leaving the card visibly selected, so it takes the brand
        border + focus ring rather than the selectable card's tint.

        `role="link"` + `tabIndex` + Enter/Space are NOT extra scope — they are what
        makes the `:focus-visible` half of that rule reachable at all. The support card
        declares the identical selector on a plain `div`, which can never receive focus,
        so the rule has always been dead there; copying the card without them would have
        given the feed a hover state no keyboard user could ever trigger, on the one
        surface whose only route to the content is that click.
      */
      {...(isFeed && {
        role: 'link',
        tabIndex: 0,
        onClick: openDetail,
        onKeyDown: (event: React.KeyboardEvent) => {
          // Enter AND Space: `role="link"` implies Enter, but the element is a div, so
          // neither key does anything unless handled. Space is included because the
          // card reads as a button to most people, and preventDefault stops the page
          // scrolling underneath the activation.
          if (event.key !== 'Enter' && event.key !== ' ') return
          event.preventDefault()
          openDetail()
        },
      })}
      sx={
        // Only the FEED draws a card. The dialog supplies its own frame and the detail
        // page is the frame — a border in either case would be a box in a box.
        isFeed
          ? {
              p: 5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary[100]}`,
              bgcolor: 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.15s ease-in',
              outline: 'none',
              '&:hover, &:focus-visible': {
                borderColor: theme.palette.brand[600],
                boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
              },
            }
          : {}
      }
    >
      {/* Kind chip + date. Not a `status` chip from the shared statuses map — this is
          a content label, not a lifecycle state, and reusing the status treatment
          would imply an announcement moves between these. */}
      {/* Kind chip LEFT, date RIGHT — parity with the Support surface, where the
          date is also the trailing element of its header row. It sat immediately
          after the chip, which made the two read as one compound label and left the
          rest of the row empty; pushing the date to the edge gives the chip and the
          timestamp each their own end of the line. */}
      <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
        {/* The SHARED badge (VRG-332). It used to be hand-rolled here, with its own
            private icon/tone tables — and the internal authoring list rendered the same
            kind as bare lowercase text. Extracting it is §36: the second surface is a
            shared component, not a second copy of the tone mapping. */}
        <AnnouncementKindBadge kind={announcement.kind} />
        {/* body4 (12px), as on the support card — the date is metadata, not content. */}
        <Typography variant="body4" color="text.secondary" sx={{ flexShrink: 0 }}>
          {new Date(announcement.publishedAt).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </Typography>
      </Stack>

      {/* No title on `detail`: the page's Header renders it as the page title, and a
          second copy immediately beneath would be the same words twice. The title is
          not being HIDDEN — it moved to the frame, which is exactly the chrome/content
          split this variant is allowed to make. */}
      {!isDetail &&
        (isFeed ? (
          /*
            Plain text, NOT a link — the whole card navigates (see the root Stack), so a
            linked title would be a second control with the same destination inside the
            first, and the hover states would fight: an underline appearing under the
            title while the card's own border lit up.

            `body2` + 600, matching the support request card's scale rather than a
            heading's. This was `h5` — 24px against that card's 14px subject — which
            made two lists of the same kind of record, reachable from each other via the
            Help menu, read at completely different sizes. One step above the 14px body
            below it is enough hierarchy here, because unlike a support subject (which
            IS the content) this title has a body under it to be distinguished from.
          */
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
            {announcement.title}
          </Typography>
        ) : (
          <Typography variant="h4">{announcement.title}</Typography>
        ))}

      {/* lineHeight 1.5 so descriptive copy does not read dense (§15).
          ─────────────────────────────────────────────────────────────────────────
          THE BODY IS NOW RICH TEXT, AND THE OLD NOTE HERE WAS RIGHT (VRG-332).

          What stood here said the body was plain text rendered with `pre-line`
          "without accepting HTML — the body is authored in a plain textarea and
          rendering it as markup would make the internal form an injection surface into
          every brand's app." That reasoning has not been refuted; it has been converted
          from a design constraint into a mitigation that has to be real, because this
          card is the single place an announcement reaches a customer and there is no
          per-tenant blast radius to fall back on.

          So the body is sanitised HERE, on RENDER, against a tag allow-list — not only
          on save. Every row already in the database was written by a code path that
          validated nothing, so a write-side filter would be trusting data it never saw;
          sanitising on render is idempotent and applies to every row whatever wrote it.
          See `utils/announcementBody` for the allow-list and what it excludes.

          LEGACY BODIES still render correctly. They contain real newlines that
          `pre-line` was preserving, so a body with no allow-listed tag takes the plain
          branch: React renders it as a TEXT NODE (escaped) with `pre-line` still on, i.e.
          byte-for-byte what it did before. Rich bodies drop `pre-line`, because tiptap
          already expresses breaks as `<p>` and `<br>` and leaving it on would add the
          newlines BETWEEN those tags as visible blank space. */}
      <Typography
        ref={setBodyRef}
        // body3 (14px) — the SAME size as a support request's subject line.
        variant="body3"
        color="text.secondary"
        {...(isRich
          ? { dangerouslySetInnerHTML: { __html: sanitizeAnnouncementBody(announcement.body) } }
          : { children: announcement.body })}
        sx={{
          lineHeight: 1.5,
          ...(isRich
            ? {
                // Rich text arrives as block elements. Collapse the browser's default
                // paragraph margins to one consistent gap, and keep the first and last
                // flush so the card's own `spacing={3}` stays the only vertical rhythm
                // — otherwise every rich body would sit lower in its card than every
                // legacy one, which is the drift a shared card exists to prevent.
                '& > :first-of-type': { mt: 0 },
                '& > :last-child': { mb: 0 },
                '& p': { m: 0, mb: 2 },
                '& ul, & ol': { m: 0, mb: 2, pl: 5 },
                '& li': { mb: 0.5 },
                // Headings are allowed but must not shout inside a card whose title is
                // already the heading. One step up from the body, bold, no more.
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  m: 0,
                  mb: 2,
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'text.primary',
                },
                '& blockquote': {
                  m: 0,
                  mb: 2,
                  pl: 3,
                  borderLeft: `2px solid ${theme.palette.primary[100]}`,
                },
                '& code': {
                  fontFamily: 'monospace',
                  fontSize: 13,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                },
                '& pre': { m: 0, mb: 2, p: 3, borderRadius: 1, overflowX: 'auto' },
                // Prose links inside an announcement body — same treatment as the docs
                // renderer, via the shared helper rather than a second copy of the
                // literal (which was AA-failing at 3.55:1 on the dark card).
                '& a': { color: brandText(theme) },
              }
            : { whiteSpace: 'pre-line' }),
          ...(isFeed && {
            /*
              `-webkit-line-clamp`, the only reliable way to truncate at a LINE COUNT.
              Prefixed and paired with `display: -webkit-box`, which is not a legacy
              artefact — it is the supported form in every current engine, Firefox
              included, and there is no unprefixed equivalent.

              `overflow: hidden` is what makes the measurement above work: without it
              `scrollHeight` equals `clientHeight` and nothing ever reports as
              overflowing, so "Read more" would never appear on any card.

              NOT `WebkitBoxOrient` alone — the box orientation and the clamp are both
              required, and dropping either silently renders the full body.
            */
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: FEED_BODY_LINES,
            overflow: 'hidden',
          }),
        }}
      />

      {/* Overflow-only, by measurement (see `overflows`). A card whose body fits gets
          nothing here — no link, and no empty row where one would have been, because
          the parent Stack's gap only applies between rendered children. */}
      {isFeed && overflows && (
        <Link
          component="button"
          type="button"
          onClick={openDetail}
          sx={{ width: 'fit-content', fontSize: 14, fontWeight: 600 }}
        >
          Read more
        </Link>
      )}

      {/* "You asked for this." The payoff of the whole feature and the reason the
          board-item join exists: the person who filed the request sees that THEIR
          request is the one that shipped. Only their own company's items reach here —
          the API scopes the titles, because the feature board is company-private. */}
      {announcement.requested.length > 0 && (
        <Box
          sx={{
            p: 3,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.brand[500], 0.06),
          }}
        >
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {announcement.requested.length === 1
              ? 'You asked for this: '
              : 'You asked for these: '}
            {/*
              LINKS, to THE REQUEST — the thing the reader actually filed.

              Two wrong destinations preceded this one, and the second was a
              regression. Originally `/support/requests?item=<boardItemId>`: the
              feature board, retired by VRG-324, so the link began redirecting to the
              generic company request LIST. That is not a broken link, which is what
              made it easy to miss — it lands somewhere real, just not on the thing the
              sentence names. "You asked for this" that drops you in a list of
              everything you have ever asked for is a dead end wearing a destination's
              clothes.

              Now `ROUTES.SUPPORT_REQUEST(item.requestId)`. The shape mismatch that
              blocked this — the card holds a BOARD ITEM id and needs a REQUEST id — is
              resolved server-side (`requestedItems`), where the join already lives and
              where the merge roll-up and the permission fallback can be applied
              honestly. So `requestId` is the caller's OWN request wherever they have
              one, which on a multi-company board item is the only one that answers the
              sentence.

              `requestsRoute` survives as the FALLBACK, for the case `requestId` is null
              — a staff-authored board item, or a colleague's request this reader may not
              open. Falling back to the list is right there: it is where their requests
              are, and it beats an inert link in the middle of a sentence.

              Carries the referrer (§42) so the destination's header offers "← Back to
              What's new" instead of stranding the reader on a page they arrived at
              sideways.
            */}
            {announcement.requested.map((item, i) => {
              const target = item.requestId
                ? ROUTES.SUPPORT_REQUEST(item.requestId)
                : requestsRoute

              return (
              <React.Fragment key={item.boardItemId}>
                {i > 0 && ' · '}
                <Link
                  component="button"
                  type="button"
                  // Inert only while the FALLBACK is still resolving — a request link
                  // needs no permission lookup, so it is live from the first frame.
                  // Rendered either way rather than vanishing out of the middle of a
                  // sentence it is part of.
                  disabled={!target}
                  onClick={event => {
                    // Same reason as the CTA: this link leaves for the request, not
                    // the detail page the surrounding card points at.
                    event.stopPropagation()
                    if (!target) return
                    onActioned?.()
                    navigate(target, { state: referrer })
                  }}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    verticalAlign: 'baseline',
                    textDecorationColor: alpha(theme.palette.text.primary, 0.4),
                  }}
                >
                  {item.title}
                </Link>
              </React.Fragment>
              )
            })}
          </Typography>
        </Box>
      )}

      {/* `!isModal` — the dialog renders this same button in its FOOTER, where a
          dialog's actions belong and where it can be the single rightmost primary
          beside "Got it". Not a case of the modal losing the CTA: see the docblock. */}
      {hasCta && !isModal && (
        <Stack direction="row" spacing={3}>
          {/* Secondary in the FEED, primary on the detail page.

              `primary` asserts "this is THE action of this surface". On the archive
              that claim is false eight times over — eight cards, eight primaries, so
              none of them is one, and the accent that should mark the single most
              important control on screen is spent on a list (§26/§33: one primary per
              surface). The detail page IS one announcement with one action, so there
              the claim is true and it stays primary. */}
          <Button variant={isFeed ? 'secondary' : 'primary'} onClick={onCta}>
            {ctaLabel}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
